import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';
import { withSyncToWebStorage } from '@abook-workspace/abook-library/store-features';
import {
  CheckoutJourney,
  JourneyPageConfig,
  JourneyZone,
  CheckoutStep,
  JourneyActionOverride,
} from './models/checkout-journey.types';
import { AvailabilityType, CustomerJourneyType } from './models/checkout.model';
import { getJourneyConfig, getStepIndicatorConfig } from './config/checkout-journeys.config';
import { getAvailabilityTypeZoneConfig } from './config/availability-type.config';
import {
  combineAvailabilityAndJourneyConfigs,
  applyPageOverrides,
} from './config/zone-config.merger';

interface JourneyState {
  currentJourney: CustomerJourneyType;
  currentPageIndex: number;
  availabilityType: AvailabilityType | null;
}

export const JourneyStore = signalStore(
  { providedIn: 'root' },

  withState<JourneyState>({
    currentJourney: CustomerJourneyType.BOOKING,
    currentPageIndex: 0,
    availabilityType: null,
  }),

  withSyncToWebStorage({
    key: 'abook:journey-store',
    type: 'session',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
  }),

  withComputed((state) => ({
    journeyConfig: computed((): CheckoutJourney => getJourneyConfig(state.currentJourney())),

    currentPage: computed((): JourneyPageConfig | undefined => {
      const config = getJourneyConfig(state.currentJourney());
      return config?.page[state.currentPageIndex()];
    }),

    canNavigatePrevious: computed(() => state.currentPageIndex() > 0),

    canNavigateNext: computed((): boolean => {
      const config = getJourneyConfig(state.currentJourney());
      if (!config) return false;
      const navigablePages = config.page.filter((p) => p.isNavigable !== false);
      const currentNavigableIndex = navigablePages.findIndex(
        (p) => p.pageRoute === config.page[state.currentPageIndex()]?.pageRoute,
      );
      return currentNavigableIndex !== -1 && currentNavigableIndex < navigablePages.length - 1;
    }),

    isLastPage: computed((): boolean => {
      const config = getJourneyConfig(state.currentJourney());
      return !!config && state.currentPageIndex() === config.page.length - 1;
    }),

    stepIndicatorSteps: computed((): CheckoutStep[] =>
      getStepIndicatorConfig(state.currentJourney()),
    ),

    heroSubtitle: computed((): string => {
      const config = getJourneyConfig(state.currentJourney());
      return config?.page[state.currentPageIndex()]?.heroSubtitle ?? '';
    }),
  })),

  withMethods((store) => {
    const router = inject(Router);

    return {
      setJourney(journeyType: CustomerJourneyType): void {
        patchState(store, { currentJourney: journeyType, currentPageIndex: 0 });
      },

      setJourneyAvailabilityType(availabilityType: AvailabilityType): void {
        patchState(store, { availabilityType });
      },

      setCurrentPageByRoute(route: string): void {
        const config = store.journeyConfig();
        const idx = config?.page.findIndex((p) => p.pageRoute === route) ?? -1;
        if (idx !== -1) patchState(store, { currentPageIndex: idx });
      },

      getNextPageRoute(): string | null {
        const config = store.journeyConfig();
        if (!config) return null;
        // Find the next page after the current index that is navigable
        for (let i = store.currentPageIndex() + 1; i < config.page.length; i++) {
          if (config.page[i].isNavigable !== false) {
            return config.page[i].pageRoute;
          }
        }
        return null;
      },

      getPreviousPageRoute(): string | null {
        const config = store.journeyConfig();
        if (!config) return null;
        // Find the previous page before the current index that is navigable
        for (let i = store.currentPageIndex() - 1; i >= 0; i--) {
          if (config.page[i].isNavigable !== false) {
            return config.page[i].pageRoute;
          }
        }
        return null;
      },

      validateCurrentPage(): boolean {
        const page = store.currentPage();
        if (!page?.canActivate) return true;
        return page.canActivate();
      },

      getZoneConfig<T>(zone: JourneyZone, pageOverrides?: JourneyActionOverride[]): T | undefined {
        const page = store.currentPage();
        const availabilityType = store.availabilityType();
        const pageRoute = page?.pageRoute;
        if (!pageRoute) return undefined;

        const baseConfig = combineAvailabilityAndJourneyConfigs<T>(
          zone,
          getAvailabilityTypeZoneConfig<T>(availabilityType, pageRoute, zone),
          page?.zoneConfig?.[zone] as T | undefined,
        );

        return applyPageOverrides(zone, baseConfig, pageOverrides);
      },

      async navigateNext(): Promise<boolean> {
        const next = this.getNextPageRoute();
        if (next) {
          await router.navigate(['/checkout', next]);
          return true;
        }
        return false;
      },

      async navigatePrevious(): Promise<boolean> {
        const prev = this.getPreviousPageRoute();
        if (prev) {
          await router.navigate(['/checkout', prev]);
          return true;
        }
        return false;
      },
    };
  }),
);
